// @ts-nocheck
/**
 * ApiControl - API配置管理组件
 *
 * 功能：
 * - 查看和管理API服务商配置
 * - 更新API密钥和设置
 * - 测试API连接
 */

import React, { useState, useEffect } from 'react';
import { apiGet, apiPut, apiPost } from '../../utils/apiClient';

interface ApiControlProps {
  onRefresh?: () => void;
}

interface ApiConfig {
  config_key: string;
  name: string;
  provider: string;
  default_model: string;
  is_active: boolean;
  daily_limit: number | null;
  monthly_limit: number | null;
  current_daily_cost: number;
  current_monthly_cost: number;
  priority: number;
  base_url: string;
  has_api_key: boolean;
}

interface EditFormState {
  api_key: string;
  base_url: string;
  default_model: string;
  is_active: boolean;
  daily_limit: string;
  monthly_limit: string;
}

export default function ApiControl({ onRefresh }: ApiControlProps) {
  const [configs, setConfigs] = useState<any[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<any>(null);
  const [editForm, setEditForm] = useState<EditFormState>({} as EditFormState);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/api/admin/config/apis') as any;
      if (Array.isArray(response)) {
        setConfigs(response);
      }
    } catch (error: any) {
      console.error('获取API配置列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectConfig = async (config: any) => {
    try {
      const response = await apiGet(`/api/admin/config/apis/${config.config_key}`) as any;
      if (response.success) {
        setSelectedConfig(response.config);
        setEditForm({
          api_key: '',
          base_url: response.config.base_url,
          default_model: response.config.default_model,
          is_active: response.config.is_active !== false,
          daily_limit: response.config.daily_limit || '',
          monthly_limit: response.config.monthly_limit || '',
        });
      }
    } catch (error: any) {
      console.error('获取API配置详情失败:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedConfig) return;

    try {
      setSaving(true);

      // 只发送有变更的字段
      const updates: Record<string, any> = {};
      if (editForm.api_key) updates.api_key = editForm.api_key;
      if (editForm.base_url !== selectedConfig.base_url) updates.base_url = editForm.base_url;
      if (editForm.default_model !== selectedConfig.default_model) updates.default_model = editForm.default_model;
      if (editForm.is_active !== selectedConfig.is_active) updates.is_active = editForm.is_active;
      if (editForm.daily_limit) updates.daily_limit = parseFloat(editForm.daily_limit);
      if (editForm.monthly_limit) updates.monthly_limit = parseFloat(editForm.monthly_limit);

      if (Object.keys(updates).length === 0) {
        alert('没有需要更新的内容');
        return;
      }

      const response = await apiPut(
        `/api/admin/config/apis/${selectedConfig.config_key}`,
        updates
      ) as any;

      if (response.success) {
        alert('保存成功');
        fetchConfigs();
        onRefresh?.();
        // 清空密钥输入
        setEditForm(prev => ({ ...prev, api_key: '' }));
      } else {
        alert('保存失败: ' + response.message);
      }
    } catch (error: any) {
      console.error('保存失败:', error);
      alert('保存失败: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!selectedConfig) return;

    try {
      setTesting(true);
      const response = await apiPost(`/api/admin/config/apis/${selectedConfig.config_key}/test`) as any;

      if (response.success) {
        alert(`连接测试成功!\n提供商: ${response.data?.provider}\n模型: ${response.data?.model}`);
      } else {
        alert('连接测试失败: ' + response.message);
      }
    } catch (error: any) {
      console.error('测试失败:', error);
      alert('测试失败: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  const getProviderIcon = (provider: string): string => {
    const icons: Record<string, string> = {
      zhipu: '🧠',
      openai: '🤖',
      minimax: '🎵',
      default: '🔌'
    };
    return icons[provider] || icons.default;
  };

  const getUsagePercentage = (current: number, limit: number): number => {
    if (!limit || limit === 0) return 0;
    return Math.min(100, (current / limit) * 100);
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="api-control">
      <div className="admin-panel">
        <div className="panel-header">
          <h3 className="panel-title">🔌 API 配置管理</h3>
        </div>

        {/* API配置列表 */}
        <div className="card-grid">
          {configs.map((config: any) => (
            <div
              key={config.config_key}
              className={`config-card ${selectedConfig?.config_key === config.config_key ? 'selected' : ''}`}
              onClick={() => selectConfig(config)}
            >
              <div className="config-card-header">
                <span className="config-card-title">
                  {getProviderIcon(config.provider)} {config.name}
                </span>
                <span className={`badge ${config.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {config.is_active ? '启用' : '禁用'}
                </span>
              </div>
              <div className="config-card-desc">
                {config.default_model || '未设置模型'}
              </div>

              {/* 成本进度条 */}
              {config.daily_limit && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>日限额</span>
                    <span style={{ color: '#00f5d4' }}>
                      ¥{(config.current_daily_cost || 0).toFixed(2)} / ¥{config.daily_limit}
                    </span>
                  </div>
                  <div style={{
                    height: '4px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${getUsagePercentage(config.current_daily_cost, config.daily_limit)}%`,
                      height: '100%',
                      background: getUsagePercentage(config.current_daily_cost, config.daily_limit) > 80
                        ? '#ef4444'
                        : '#00f5d4',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}

              <div className="config-card-meta" style={{ marginTop: '12px' }}>
                <span>{config.provider}</span>
                <span>优先级: {config.priority}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 编辑区域 */}
      {selectedConfig && (
        <div className="admin-panel">
          <div className="panel-header">
            <h3 className="panel-title">
              {getProviderIcon(selectedConfig.provider)} {selectedConfig.name}
            </h3>
            <div className="panel-actions">
              <button
                className="btn-secondary"
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? '测试中...' : '测试连接'}
              </button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* 左列 */}
            <div>
              <div className="form-group">
                <label className="form-label">配置标识</label>
                <input
                  type="text"
                  className="form-input"
                  value={selectedConfig.config_key}
                  disabled
                />
              </div>

              <div className="form-group">
                <label className="form-label">API 密钥</label>
                <input
                  type="password"
                  className="form-input"
                  value={editForm.api_key}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, api_key: e.target.value })}
                  placeholder={selectedConfig.has_api_key ? '已设置 (留空保持不变)' : '输入 API 密钥...'}
                />
                <div className="form-hint">
                  {selectedConfig.has_api_key ? '当前已配置密钥，输入新值将覆盖' : '未配置密钥'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Base URL</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.base_url}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, base_url: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">默认模型</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.default_model}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, default_model: e.target.value })}
                />
              </div>
            </div>

            {/* 右列 */}
            <div>
              <div className="form-group">
                <label className="form-label">状态</label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: editForm.is_active ? '#10b981' : '#ef4444' }}>
                    {editForm.is_active ? '启用' : '禁用'}
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">日限额 (元)</label>
                <input
                  type="number"
                  className="form-input"
                  value={editForm.daily_limit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, daily_limit: e.target.value })}
                  placeholder="留空表示不限制"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label className="form-label">月限额 (元)</label>
                <input
                  type="number"
                  className="form-input"
                  value={editForm.monthly_limit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, monthly_limit: e.target.value })}
                  placeholder="留空表示不限制"
                  step="0.01"
                />
              </div>

              {/* 当前使用情况 */}
              <div style={{
                padding: '16px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                marginTop: '16px'
              }}>
                <div className="form-label" style={{ marginBottom: '12px' }}>当前使用情况</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>今日消耗</div>
                    <div style={{ fontSize: '18px', color: '#00f5d4', fontWeight: '600' }}>
                      ¥{(selectedConfig.current_daily_cost || 0).toFixed(4)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>本月消耗</div>
                    <div style={{ fontSize: '18px', color: '#00f5d4', fontWeight: '600' }}>
                      ¥{(selectedConfig.current_monthly_cost || 0).toFixed(4)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .config-card.selected {
          border-color: #00f5d4;
          background: rgba(0,245,212,0.05);
        }
      `}</style>
    </div>
  );
}
